// TiptapActions.tsx
import React, { useRef } from 'react';
import {
  Bold,
  Italic,
  Strikethrough,
  Code,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Minus,
  Heading4,
  Heading5,
  Heading6,
  AlignCenterIcon,
  AlignRightIcon,
  AlignLeftIcon,
  LinkIcon,
  Undo2Icon,
  Redo2Icon,
  CornerDownLeftIcon,
  TrashIcon,
} from 'lucide-react';

import { Button } from '@src/design-system/button';
import { HexColorPicker } from 'react-colorful';

import type { Editor } from '@tiptap/react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@src/design-system/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@src/design-system/popover';
import { useTiptapTextStyle } from '../hooks/use-tiptap-text-style';

import { cn } from '@src/design-system/helpers';
import { Input } from '@src/design-system/input';

export interface TiptapToolbarProps {
  editor: Editor;
  className?: string;
}

const triggerStyles = cn(
  'size-8 flex items-center bg-transparent text-gray-900 text-sm justify-center p-0 border-none cursor-pointer transition-colors rounded-lg',
  '[&_svg]:size-4',
  'hover:bg-pale-border',
  'disabled:text-gray-500 disabled:bg-transparent',
  'data-[placeholder]:text-gray-900 data-[placeholder]:text-sm',
  'data-[active=true]:bg-pale-border data-[active=true]:text-primary-1000'
);

export const TiptapToolbar: React.FC<TiptapToolbarProps> = ({
  editor,
  className,
}) => {
  const {
    FontSizeList,
    fontSize,
    linkUrl,
    color,
    activeHeading,
    handleFontSizeChange,
    handleColorChange,
    handleColorChangeInEditor,
    handleHeaders,
    handleLinkUrl,
    handleAlignmentChange,
    handleSetLink,
  } = useTiptapTextStyle({ editor });
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <div
      className={cn(
        'p-2 flex gap-px flex-wrap items-center bg-primary-100 ',
        className
      )}
      ref={containerRef}
    >
      <Select
        onValueChange={handleFontSizeChange}
        value={fontSize}
      >
        <SelectTrigger
          withIcon={false}
          className={triggerStyles}
        >
          <SelectValue placeholder='Font size' />
        </SelectTrigger>

        <SelectContent id='editor-font-size-select'>
          {FontSizeList.map((size) => (
            <SelectItem
              key={size}
              value={size}
            >
              {size}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Button
        data-active={editor.isActive('bold')}
        onClick={() => {
          editor.chain().focus().toggleBold().run();
          editor.view.dispatch(editor.state.tr);
        }}
        className={triggerStyles}
        disabled={!editor.can().chain().focus().toggleBold().run()}
      >
        <Bold />
      </Button>

      <Button
        data-active={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        disabled={!editor.can().chain().focus().toggleItalic().run()}
        className={triggerStyles}
      >
        <Italic />
      </Button>

      <Button
        data-active={editor.isActive('strike')}
        className={triggerStyles}
        onClick={() => editor.chain().focus().toggleStrike().run()}
        disabled={!editor.can().chain().focus().toggleStrike().run()}
      >
        <Strikethrough />
      </Button>

      <Button
        data-active={editor.isActive('code')}
        className={triggerStyles}
        onClick={() => editor.chain().focus().toggleCode().run()}
        disabled={!editor.can().chain().focus().toggleCode().run()}
      >
        <Code />
      </Button>

      <Popover
        onOpenChange={(open) => {
          if (!open) handleColorChangeInEditor();
        }}
      >
        <PopoverTrigger asChild>
          <Button className={triggerStyles}>
            <div
              className='size-2 rounded-full'
              style={{ backgroundColor: color }}
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className='w-auto p-2'
          withPortal={false}
        >
          <HexColorPicker
            color={color}
            onChange={handleColorChange}
          />
        </PopoverContent>
      </Popover>

      <Select
        {...(activeHeading
          ? { value: String(activeHeading) }
          : { defaultValue: undefined })}
        onValueChange={(value) =>
          handleHeaders(Number(value) as 1 | 2 | 3 | 4 | 5 | 6)
        }
      >
        <SelectTrigger
          withIcon={false}
          className={triggerStyles}
        >
          <SelectValue placeholder='H' />
        </SelectTrigger>

        <SelectContent id='editor-headers-select'>
          {[1, 2, 3, 4, 5, 6].map((level) => (
            <SelectItem
              key={level}
              value={String(level)}
            >
              <div className='flex items-center gap-2'>
                {level === 1 ? (
                  <Heading1 size={24} />
                ) : level === 2 ? (
                  <Heading2 size={20} />
                ) : level === 3 ? (
                  <Heading3 size={18} />
                ) : level === 4 ? (
                  <Heading4 size={16} />
                ) : level === 5 ? (
                  <Heading5 size={15} />
                ) : (
                  <Heading6 size={14} />
                )}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Button
        data-active={editor.isActive({ textAlign: 'left' })}
        onClick={() => handleAlignmentChange('left')}
        className={triggerStyles}
      >
        <AlignLeftIcon />
      </Button>

      <Button
        data-active={editor.isActive({ textAlign: 'center' })}
        onClick={() => handleAlignmentChange('center')}
        className={triggerStyles}
      >
        <AlignCenterIcon />
      </Button>

      <Button
        data-active={editor.isActive({ textAlign: 'right' })}
        onClick={() => handleAlignmentChange('right')}
        className={triggerStyles}
      >
        <AlignRightIcon />
      </Button>

      <Popover>
        <PopoverTrigger asChild>
          <Button
            data-active={editor.isActive('link')}
            className={triggerStyles}
          >
            <LinkIcon />
          </Button>
        </PopoverTrigger>

        <PopoverContent className='w-64 p-3 flex gap-2'>
          <Input
            type='url'
            onChange={(e) => handleLinkUrl(e.target.value)}
            value={linkUrl}
            size='xs'
          />
          <div className='flex'>
            <Button
              disabled={!linkUrl}
              size='iconXs'
              variant='ghost'
              className='text-sm shrink-0 rounded-lg'
              onClick={handleSetLink}
            >
              <CornerDownLeftIcon />
            </Button>

            <Button
              disabled={!linkUrl}
              size='iconXs'
              variant='ghost'
              className='text-sm shrink-0 rounded-lg'
              onClick={() =>
                editor.chain().focus().extendMarkRange('link').unsetLink().run()
              }
            >
              <TrashIcon />
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      <Button
        data-active={editor.isActive('bulletList')}
        className={triggerStyles}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <List />
      </Button>

      <Button
        data-active={editor.isActive('orderedList')}
        className={triggerStyles}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <ListOrdered />
      </Button>

      <Button
        data-active={editor.isActive('blockquote')}
        className={triggerStyles}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
      >
        <Quote />
      </Button>

      <Button
        className={triggerStyles}
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
      >
        <Minus />
      </Button>

      <Button
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().chain().focus().undo().run()}
        className={triggerStyles}
      >
        <Undo2Icon />
      </Button>

      <Button
        className={triggerStyles}
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().chain().focus().redo().run()}
      >
        <Redo2Icon />
      </Button>
    </div>
  );
};
